import { PropsWithChildren, ReactNode, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MdErrorOutline, MdOutlineEvent, MdOutlineSettings } from 'react-icons/md';
import { IconButton, Switch, useDisclosure } from '@ds';
import { useScheduleMutationsLoader } from '@lp/features/Scheduler/hooks';
import { SchedulerMutationActions } from '@lp/features/Scheduler/types';
import { SchedulerConfirmationModal, ScheduleStatusBadge } from '@lp/features/Scheduler/components';
import {
  MonthSchedule,
  PlanningProcess,
  ScheduleFillingState,
  SchedulePublishReadiness,
} from '@/api/apis/resources/schedule';
import { ControlButtons, MonthNavigationButtons } from './components';

type SchedulerControlHeaderProps = PropsWithChildren<{
  readonly?: boolean;
  disableAutogeneration?: boolean;
  disablePlanningToggle?: boolean;
  hidePlanningToggle?: boolean;
  hideControlButtons?: boolean;
  disableSettings?: boolean;
  settingsPath: string;
  actions: SchedulerMutationActions;
  monthSchedule?: MonthSchedule;
  isPlanningMode: boolean;
  togglePlanningMode: (value?: boolean) => void;
  planningData?: PlanningProcess;
  violationsCount?: number;
  validationIsFetching?: boolean;
  publishReadinessData?: SchedulePublishReadiness;
  fillingState?: ScheduleFillingState;
  conflictsButton?: ReactNode;
  hrConflictsButton?: ReactNode;
  publishBlockerTooltip?: ReactNode;
  startDay: Date;
  selectedDate: Date;
  /** Optional label to display instead of the default month name (e.g. 28-day period). */
  periodLabel?: string;
  /** Optional date-range line shown beneath the period label (e.g. "13 August – 9 September"). */
  periodDateRange?: string | null;
  onMonthPrev: () => void;
  onMonthNext: () => void;
}>;

export const SchedulerControlHeader = ({
  readonly,
  disableAutogeneration,
  disablePlanningToggle,
  hidePlanningToggle,
  hideControlButtons,
  disableSettings,
  settingsPath,
  actions,
  monthSchedule,
  planningData,
  violationsCount,
  validationIsFetching,
  publishReadinessData,
  fillingState,
  conflictsButton,
  hrConflictsButton,
  publishBlockerTooltip,
  isPlanningMode,
  togglePlanningMode,
  startDay,
  selectedDate,
  periodLabel,
  periodDateRange,
  onMonthPrev,
  onMonthNext,
  children,
}: SchedulerControlHeaderProps) => {
  const [searchParams] = useSearchParams();
  const [isMutating] = useScheduleMutationsLoader();
  const discardPlanningModal = useDisclosure();
  const isPublished = !!monthSchedule?.hasSchedule && !!monthSchedule.scheduleId;
  const isPlannable = monthSchedule?.hasPreSchedule && !monthSchedule.scheduleId;
  const withPlanning = !!monthSchedule?.hasPlanningProcess && isPlannable;
  const canCreatePlanning = !monthSchedule?.hasPlanningProcess && isPlannable;
  const showPlanningToggle = withPlanning && !hidePlanningToggle;
  const showPlanningAlert = withPlanning && hidePlanningToggle && isPlanningMode;

  const onPlanningCreated = useCallback(() => togglePlanningMode(true), [togglePlanningMode]);

  if (readonly) {
    return (
      <div className="flex items-center gap-4">
        <MonthNavigationButtons
          selectedDate={selectedDate}
          label={periodLabel}
          dateRange={periodDateRange}
          onMonthPrev={onMonthPrev}
          onMonthNext={onMonthNext}
        >
          <ScheduleStatusBadge monthSchedule={monthSchedule} readonly isPeriodMode={!!periodDateRange} />
        </MonthNavigationButtons>
        <div className="flex items-center justify-between gap-4 grow">{children}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between gap-2">
        <MonthNavigationButtons
          selectedDate={selectedDate}
          label={periodLabel}
          dateRange={periodDateRange}
          onMonthPrev={onMonthPrev}
          onMonthNext={onMonthNext}
        >
          {showPlanningToggle && (
            <Switch
              size="lg"
              isDisabled={disablePlanningToggle || isMutating}
              isSelected={isPlanningMode}
              onValueChange={togglePlanningMode}
              thumbIcon={<MdOutlineEvent />}
              classNames={{
                wrapper: 'rounded-lg',
                thumb: 'rounded-md text-lg',
              }}
            />
          )}
          {showPlanningAlert ? (
            <>
              <div className="flex gap-2 items-center bg-info-70 ps-2 pe-3 h-8 rounded-small">
                <MdErrorOutline size={20} className="text-info-40" />
                <div className="text-s-m text-info-5">
                  <span>Planning Mode started. The other users are not able to request shifts. </span>
                  <button className="text-danger-30 underline" onClick={discardPlanningModal.onOpen}>
                    Discard Planning
                  </button>
                </div>
              </div>
              <SchedulerConfirmationModal
                isOpen={discardPlanningModal.isOpen}
                title="Discard Planning?"
                confirmText="Discard Planning"
                discardText="Cancel"
                confirmButtonProps={{ color: 'danger' }}
                onConfirm={actions.onPlanningDiscard}
                onClose={discardPlanningModal.onClose}
              >
                The entire system will exit planning mode, all unpublished changes will be deleted irretrievably. Are
                you sure you want to discard planning?
              </SchedulerConfirmationModal>
            </>
          ) : (
            <ScheduleStatusBadge
              monthSchedule={monthSchedule}
              disableAutogeneration={disableAutogeneration}
              isPeriodMode={!!periodDateRange}
            />
          )}
        </MonthNavigationButtons>
        <div className="flex items-center gap-2">
          {!hideControlButtons && (
            <ControlButtons
              startDay={startDay}
              actions={actions}
              disableAutogeneration={disableAutogeneration}
              hidePlanningToggle={hidePlanningToggle}
              isPlanningMode={isPlanningMode}
              planningData={planningData}
              violationsCount={violationsCount}
              validationIsFetching={validationIsFetching}
              publishReadinessData={publishReadinessData}
              fillingState={fillingState}
              conflictsButton={conflictsButton}
              publishBlockerTooltip={publishBlockerTooltip}
              canCreatePlanning={canCreatePlanning}
              onPlanningCreated={onPlanningCreated}
              periodLabel={periodLabel}
              periodDateRange={periodDateRange}
            />
          )}
          {isPublished && hrConflictsButton}
          {!disableSettings && (
            <IconButton
              as={Link}
              size="md"
              // The query string goes in the URL as well as in the state: the state is what the
              // "Back to Schedule" link is rebuilt from, but it does not survive a reload, and the
              // CS settings page picks its settings family from `day`.
              to={`${settingsPath}?${searchParams}`}
              state={Object.fromEntries(searchParams)}
              variant="outline"
              startContent={<MdOutlineSettings size={24} />}
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
};
